// src/pages/PreferencesPage.tsx
/**
 * Full preferences review and editing page
 * Allows users to view and edit all preference sections
 * ⚠️ FIXED: Handles nested preference_data structure from backend
 */
import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonCardContent,
  IonText,
  IonChip,
  IonIcon,
  IonButton,
  IonSpinner,
  useIonToast,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
} from '@ionic/react';
import {
  saveOutline,
  checkmarkCircle,
  warningOutline,
  sparklesOutline,
  trendingUpOutline,
} from 'ionicons/icons';
import PreferenceService from '../services/PreferenceService.ts';
import {
  UserPreferences,
  PreferenceSectionConfig,
  getConfidenceColor,
  getConfidenceLabel,
  PREFERENCE_SECTIONS,
} from '../types/preferences.ts';
import './PreferencesPage.css';

// Import section editors
import JobPreferenceEditor from '../components/preferences/JobPreferenceEditor.tsx';
import LocationPreferenceEditor from '../components/preferences/LocationPreferenceEditor.tsx';
import SkillPreferenceEditor from '../components/preferences/SkillPreferenceEditor.tsx';
import IndustriesPreferenceEditor from '../components/preferences/IndustriesPreferenceEditor.tsx';
import CompanyPreferenceEditor from '../components/preferences/CompanyPreferenceEditor.tsx';
import CompensationPreferenceEditor from '../components/preferences/CompensationPreferenceEditor.tsx';
import WorkStylePreferenceEditor from '../components/preferences/WorkStylePreferenceEditor.tsx';
import CareerGoalsPreferenceEditor from '../components/preferences/CareerGoalsPreferenceEditor.tsx';

const PreferencesPage: React.FC = () => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [present] = useIonToast();

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const data = await PreferenceService.getPreferences();
      setPreferences(data);
    } catch (error: any) {
      console.error('Failed to load preferences:', error);

      // If preferences don't exist (404), create empty ones
      if (error.response?.status === 404) {
        try {
          const emptyPrefs = {
            job: { roles: [], seniority: 'mid', family: '', search_status: 'actively_looking' },
            locations: { remote: false, hybrid_ok: false, relocation_willing: false, cities: [] },
            skills: { required: [], nice_to_have: [], learning: [] },
            industries: [],
            company: { stages: [], sizes: [], values: [] },
            compensation: { currency: 'USD', min: undefined, max: undefined, equity_important: false, benefits_priorities: [] },
            work_style: { work_hours: '', team_size: '', autonomy_level: '', meeting_culture: '' },
            career_goals: { short_term: '', long_term: '', growth_areas: [] },
          };
          const response = await PreferenceService.updatePreferences(emptyPrefs);
          setPreferences(response.updated_preference);
          present({
            message: 'Created new preference profile. Start filling it out!',
            duration: 3000,
            color: 'success',
          });
        } catch (createError) {
          console.error('Failed to create preferences:', createError);
          present({
            message: 'Failed to create preferences',
            duration: 3000,
            color: 'danger',
          });
        }
      } else {
        present({
          message: error.response?.data?.detail || 'Failed to load preferences',
          duration: 3000,
          color: 'danger',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadPreferences();
    event.detail.complete();
  };

  const handleSectionUpdate = (sectionName: string, sectionData: any) => {
    if (!preferences) return;

    // ⚠️ FIXED: Update nested preference_data structure
    setPreferences((prev) => ({
      ...prev!,
      preference_data: {
        ...prev!.preference_data,
        [sectionName]: sectionData,
      },
    }));
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    if (!preferences) return;

    try {
      setSaving(true);
      // ⚠️ FIXED: Send only preference_data, not full object
      const response = await PreferenceService.updatePreferences(preferences.preference_data);
      // Backend returns PreferenceMergeResponse with updated_preference field
      setPreferences(response.updated_preference);
      setHasChanges(false);
      present({
        message: 'Preferences saved successfully!',
        duration: 2000,
        color: 'success',
        icon: checkmarkCircle,
      });
    } catch (error: any) {
      console.error('Failed to save preferences:', error);
      present({
        message: error.response?.data?.detail || 'Failed to save preferences',
        duration: 3000,
        color: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const getSectionEditor = (section: PreferenceSectionConfig, sectionData: any) => {
    const commonProps = {
      data: sectionData || {},
      onChange: (data: any) => handleSectionUpdate(section.key, data),
    };

    // ⚠️ FIXED: Use correct section keys matching PREFERENCE_SECTIONS
    switch (section.key) {
      case 'job':
        return <JobPreferenceEditor {...commonProps} />;
      case 'locations':
        return <LocationPreferenceEditor {...commonProps} />;
      case 'skills':
        return <SkillPreferenceEditor {...commonProps} />;
      case 'industries':
        return <IndustriesPreferenceEditor {...commonProps} />;
      case 'company':
        return <CompanyPreferenceEditor {...commonProps} />;
      case 'compensation':
        return <CompensationPreferenceEditor {...commonProps} />;
      case 'work_style':
        return <WorkStylePreferenceEditor {...commonProps} />;
      case 'career_goals':
        return <CareerGoalsPreferenceEditor {...commonProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/app/profile" />
            </IonButtons>
            <IonTitle>Preferences</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="preferences-loading">
            <IonSpinner name="crescent" />
            <IonText color="medium">
              <p>Loading your preferences...</p>
            </IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/profile" />
          </IonButtons>
          <IonTitle>My Preferences</IonTitle>
          <IonButtons slot="end">
            {hasChanges && (
              <IonButton onClick={handleSaveAll} disabled={saving}>
                <IonIcon slot="start" icon={saveOutline} />
                {saving ? 'Saving...' : 'Save'}
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <IonGrid className="preferences-grid">
          <IonRow>
            <IonCol size="12">
              {/* Overall confidence indicator */}
              {preferences && preferences.metadata && (
                <IonCard className="confidence-card" style={{
                  background: isNaN(preferences.metadata.overall_confidence) || preferences.metadata.overall_confidence === 0
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : preferences.metadata.overall_confidence >= 0.8
                    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                    : preferences.metadata.overall_confidence >= 0.6
                    ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                    : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  borderRadius: '16px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                  margin: '0 0 20px 0'
                }}>
                  <IonCardContent style={{ padding: '24px' }}>
                    <div className="confidence-header" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px'
                    }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.25)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <IonIcon
                          icon={
                            isNaN(preferences.metadata.overall_confidence) || preferences.metadata.overall_confidence === 0
                              ? sparklesOutline
                              : preferences.metadata.overall_confidence >= 0.8
                              ? checkmarkCircle
                              : trendingUpOutline
                          }
                          style={{
                            fontSize: '32px',
                            color: 'white'
                          }}
                        />
                      </div>
                      <IonText style={{ flex: 1 }}>
                        <h2 style={{
                          margin: '0 0 6px 0',
                          fontSize: '22px',
                          fontWeight: '700',
                          color: 'white',
                          letterSpacing: '-0.5px'
                        }}>
                          {getConfidenceLabel(preferences.metadata.overall_confidence)}
                        </h2>
                        {!isNaN(preferences.metadata.overall_confidence) && preferences.metadata.overall_confidence > 0 ? (
                          <p style={{
                            margin: 0,
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}>
                            Profile completeness: {Math.round(preferences.metadata.overall_confidence * 100)}%
                          </p>
                        ) : (
                          <p style={{
                            margin: 0,
                            color: 'rgba(255, 255, 255, 0.9)',
                            fontSize: '15px',
                            fontWeight: '500'
                          }}>
                            Complete your preferences to improve job matches
                          </p>
                        )}
                      </IonText>
                    </div>
                    {preferences.metadata.low_confidence_sections &&
                      preferences.metadata.low_confidence_sections.length > 0 && (
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: '1px solid rgba(255, 255, 255, 0.3)'
                        }}>
                          <p style={{
                            margin: '0 0 8px 0',
                            color: 'white',
                            fontSize: '13px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Sections to Review
                          </p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {preferences.metadata.low_confidence_sections.map((section: string) => (
                              <IonChip
                                key={section}
                                style={{
                                  '--background': 'rgba(255, 255, 255, 0.25)',
                                  '--color': 'white',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  height: '28px',
                                  margin: 0
                                }}
                              >
                                {section.replace(/_/g, ' ')}
                              </IonChip>
                            ))}
                          </div>
                        </div>
                      )}
                  </IonCardContent>
                </IonCard>
              )}

              {/* Preference sections */}
              {PREFERENCE_SECTIONS.map((section) => {
                // ⚠️ FIXED: Read from nested preference_data
                const sectionData = preferences?.preference_data?.[section.key as keyof typeof preferences.preference_data];
                const confidence = preferences?.metadata?.section_confidence?.[section.key];

                return (
                  <IonCard key={section.key} className="preference-section-card">
                    <IonCardHeader>
                      <div className="section-header">
                        <div className="section-title">
                          <IonIcon
                            icon={section.icon}
                            style={{ color: section.color }}
                            className="section-icon"
                          />
                          <IonText>
                            <h3>{section.emoji} {section.title}</h3>
                            <p className="section-description">{section.description}</p>
                          </IonText>
                        </div>
                        {confidence !== undefined && (
                          <IonChip color={getConfidenceColor(confidence)} className="confidence-chip">
                            {Math.round(confidence * 100)}% confident
                          </IonChip>
                        )}
                      </div>
                    </IonCardHeader>
                    <IonCardContent>
                      {getSectionEditor(section, sectionData)}
                    </IonCardContent>
                  </IonCard>
                );
              })}
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Floating save button for mobile */}
        {hasChanges && (
          <div className="floating-save-button">
            <IonButton
              expand="block"
              onClick={handleSaveAll}
              disabled={saving}
              size="large"
            >
              <IonIcon slot="start" icon={saveOutline} />
              {saving ? 'Saving...' : 'Save All Changes'}
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default PreferencesPage;
